import Serialization, {
  type DocData,
  type CopiedWhiteboardContent,
  type PastedWhiteboardContent,
} from '../collaboration/Serialization';
import Tool from './Tool';
import version from '../version';
import { randomId } from '@aha-app/mvc';
import { fabric } from 'fabric';
import { fireCreatedDeep } from '../utils/objects';
import type WhiteboardController from '../controllers/WhiteboardController';
import paper from 'paper';
import Shape from '../objects/Shape';
import type ShapeCustomPath from '../shapes/ShapeCustomPath';
import { type RecordId } from '../shared-types';
import { type IntegrationRecordReference } from '../controllers/IntegrationsController';
import { type IPoint } from 'fabric/fabric-impl';
import { RECORD_DEFAULT_HEIGHT } from '../constants';
import Debug from 'debug';
import SVGImporter from '../utils/SVGImporter';
import { CustomShape } from '../models/CustomShape';

const debug = Debug('whiteboard');

type PasteShapes = PastedWhiteboardContent<Partial<Shape>>;

// Unsophisticated regex to check if a string is an SVG path.
const SVG_PATH_REGEX = /^[Mm]\s*\d+[MmZzLlHhVvCcSsQqTtAa\d-,.\s]+[Zz]?$/;
const SVG_REGEX = /^<svg[^>]*>.*<\/svg>$/s;
const URL_REGEX = /^https:\/\/[^/]+/;

const RECORD_REGEXES = [
  {
    recordType: 'features',
    regex: /\/features\/([A-Z][A-Z0-9]*-[0-9]+)/,
  },
  {
    recordType: 'requirements',
    regex: /\/requirements\/([A-Z][A-Z0-9]*-[0-9]+-[0-9]+)/,
  },
  {
    recordType: 'epics',
    regex: /\/epics\/([A-Z][A-Z0-9]*-E-[0-9]+)/,
  },
  {
    recordType: 'releases',
    regex: /\/releases\/([A-Z][A-Z0-9]*-R-[0-9]+)/,
  },
  {
    recordType: 'releases',
    regex: /\/master_releases\/([A-Z][A-Z0-9]*-R-[0-9]+)/,
  },
  {
    recordType: 'releasePhases',
    regex: /\/release_phases\/([0-9]+)/,
  },
  {
    recordType: 'ideas',
    regex: /\/ideas\/([A-Z][A-Z0-9]*-I-[0-9]+)/,
  },
  {
    recordType: 'ideaThemes',
    regex: /\/idea_themes\/([A-Z][A-Z0-9]*-T-[0-9]+)/,
  },
  {
    recordType: 'initiatives',
    regex: /\/initiatives\/([A-Z][A-Z0-9]*-S-[0-9]+)/,
  },
  {
    recordType: 'goals',
    regex: /\/strategic_imperatives\/([0-9]+|[A-Z][A-Z0-9]*-G-[0-9]+)/,
  },
  {
    recordType: 'pages',
    regex: /\/pages\/([A-Z][A-Z0-9]*-N-[0-9]+)/,
  },
  {
    recordType: 'tasks',
    regex: /\/tasks\/([0-9]+|[A-Z][A-Z0-9]*-G-[0-9]+)/,
  },
  {
    recordType: 'keyResults',
    regex: /\/key_results\/([A-Z][A-Z0-9]*-G-[0-9]+-KR-[0-9]+)/,
  },
  {
    recordType: 'projects',
    regex: /\/projects\/([A-Z][A-Z0-9]+)/,
  },
] as const;

// We use semantic versioning (see README.md) and only verify the major
// version matches.
function acceptableVersion(clipVersion: string) {
  return version.split('.')[0] === clipVersion.split('.')[0];
}

type ParsedRecordData =
  | {
      type: 'recordData';
      contents: PasteShapes;
    }
  | IntegrationRecordReference;

export function parseUrl(
  controller: WhiteboardController,
  rawText: string
): ParsedRecordData | undefined {
  if (!controller.allowRecordLinking()) {
    controller.actionShowMessage(
      'URLs cannot be pasted on a shared whiteboard'
    );
    return;
  }

  const integrationServices = Object.keys(
    controller.integrationsController.integrations
  );

  // Check if the URL belongs to any of the existing integrations
  for (const integrationService of integrationServices) {
    const integration =
      controller.integrationsController.getIntegration(integrationService);
    const recordId = integration?.urlToRecordId(rawText);

    if (recordId) {
      if (!window.currentAccount?.whiteboardsAdvanced) {
        controller.actionShowMessage({
          type: 'integrationUpgradePrompt',
          integration,
        });
        return;
      }

      return {
        type: 'recordReference',
        recordType: integrationService,
        recordKey: recordId,
        recordUrl: rawText,
      };
    }
  }

  const shapes: PasteShapes['objects'] = {};
  const urls = rawText.split(/\s*\n\s*/);

  // Match against Aha! record URLs
  urls.forEach(url => {
    for (const recordPattern of RECORD_REGEXES) {
      const matches = url.match(recordPattern.regex);
      if (matches) {
        const recordId = matches[1] as RecordId;
        const objectId = randomId() as RecordId;

        shapes[objectId] = {
          id: objectId,
          type: 'shape',
          shapeName: 'record',
          authorId: controller.state.userId,
          recordType: recordPattern.recordType,
          recordId,
          // Offset top to space them out
          top: Object.keys(shapes).length * (RECORD_DEFAULT_HEIGHT + 10),
          left: 0,
        };
      }
    }
  });

  if (Object.keys(shapes).length > 0) {
    return {
      type: 'recordData',
      contents: {
        objects: shapes,
        version,
      },
    };
  }

  controller.actionShowMessage(
    'The pasted URL(s) is not a supported record type'
  );
}

function textToObjects(
  controller: WhiteboardController,
  rawText: string
): PasteShapes {
  const rows = rawText
    .split(/\r|\n/)
    .map(r => r.trim())
    .filter(r => r.length > 0);

  let left = 0;
  let top = 0;
  const doc: PasteShapes = {
    objects: {},
    version,
  };
  rows.forEach((row, index) => {
    if (index > 0 && index % 4 === 0) {
      left = 0;
      top += 120;
    }

    const objectId = randomId();
    doc.objects[objectId] = {
      id: objectId,
      type: 'shape',
      shapeName: 'note',
      authorId: controller.state.userId,
      textContent: `<p style="text-align:center">${row}</p>`,
      left,
      top,
      ...controller.defaultsManager.for('shape', 'note'),
    };
    left += 120;
  });

  return doc;
}

export function parseText(
  text: string,
  controller: WhiteboardController
): ParsedRecordData | undefined {
  if (text.match(URL_REGEX)) {
    return parseUrl(controller, text);
  }

  let contents: PastedWhiteboardContent | undefined;

  if (text[0] === '{') {
    const data = JSON.parse(text) as CopiedWhiteboardContent;
    if (!data.objects || !acceptableVersion(data.version)) {
      throw new Error('Incompatible clipboard');
    } else {
      contents = Serialization.transformWhiteboardContent(
        data as PastedWhiteboardContent,
        controller
      );
    }
  } else {
    contents = textToObjects(controller, text);
  }

  if (!contents) {
    return;
  }

  return {
    type: 'recordData',
    contents,
  };
}

function createCustomPathShape({
  controller,
  pathData,
}: {
  controller: WhiteboardController;
  pathData: string;
}) {
  const path = new paper.CompoundPath(pathData);
  const position = controller.mousePosition;
  path.position = new paper.Point(position.x, position.y);

  const left = path.bounds.topLeft.x;
  const top = path.bounds.topLeft.y;
  const width = path.bounds.width;
  const height = path.bounds.height;

  path.position = new paper.Point(0, 0);

  // Don't create a shape if the path is empty.
  if (!path.pathData) {
    return;
  }

  // Check the path array that fabric uses.
  // If it's not valid, don't create a shape.
  const fabricPath = fabric.util.makePathSimpler(
    fabric.util.parsePath(path.pathData)
  );

  if (!Array.isArray(fabricPath) || fabricPath.length === 0) {
    return;
  }

  for (const command of fabricPath) {
    if (
      typeof command[0] !== 'string' ||
      !command.slice(1).every(n => n === Number(n))
    ) {
      return;
    }
  }

  const customPathShape = Shape.create<ShapeCustomPath>({
    controller,
    ...controller.defaultsManager.for('shape'),
    shapeName: 'customPath',
    left,
    top,
    width,
    height,
    angle: 0,
    scaleX: 1,
    scaleY: 1,
    shapePath: path.pathData,
  });

  controller.canvas.replaceSelectionWith(customPathShape);

  controller.actionShowObjectToolbar();
}

class ToolPaste extends Tool {
  static toolName = 'Paste objects';
  // Note: This tool doesn't have a shortcut registered, as it's manually
  // triggered by listening to `paste` events in the whiteboard controller.

  static displayedPasteWarning = false;

  static async pasteSVGTextAsShapes(
    controller: WhiteboardController,
    rawText: string
  ) {
    const previousShapeAnalyticsSetting = controller.sendShapeCreationAnalytics;
    try {
      controller.sendShapeCreationAnalytics = false;

      const importer = new SVGImporter(controller);
      const doc = await importer.import(rawText);
      const clipboardContents: ParsedRecordData = {
        type: 'recordData',
        contents: {
          ...doc,
          version,
        },
      };
      await this.handleClipboardContents(controller, clipboardContents);
    } finally {
      controller.sendShapeCreationAnalytics = previousShapeAnalyticsSetting;
    }
  }

  static async readText({ controller }: { controller: WhiteboardController }) {
    let clipboardContents: ParsedRecordData | undefined;

    // Try reading from the browser clipboard first. This allows us to
    // copy/paste between windows, and do allow copy/paste as a form of
    // persistence.
    try {
      // Check for clipboard support.
      if (!navigator.clipboard?.readText && !ToolPaste.displayedPasteWarning) {
        ToolPaste.displayedPasteWarning = true;

        controller.actionShowPasteUnsupportedMessage();
        throw new Error('Clipboard not supported');
      }

      const rawText = await navigator.clipboard.readText();
      if (!rawText && rawText !== '') {
        throw new Error('Unable to reach clipboard');
      }

      if (rawText.trim().match(SVG_PATH_REGEX)) {
        createCustomPathShape({ controller, pathData: rawText.trim() });
      } else if (rawText.trim().match(SVG_REGEX)) {
        // TODO: Pop up menu to ask user whether they want to paste as an image or as shapes.
        await this.pasteSVGTextAsShapes(controller, rawText);
        return;
      } else {
        clipboardContents = parseText(rawText, controller);
      }
    } catch (e) {
      // Log locally for troubleshooting. We don't want sentries because the
      // most common problem is Firefox, or user not allowing permission for
      // paste in Chrome.
      console.log(e);

      // It wasn't possible to read from the clipboard, fall back to using the
      // last thing copied in this whiteboard instance.
      const contents = Serialization.transformWhiteboardContent(
        controller.clipboard,
        controller
      );

      if (contents) {
        clipboardContents = {
          type: 'recordData',
          contents,
        };
      }
    }

    await this.handleClipboardContents(controller, clipboardContents);
  }

  static async pasteUrl({
    url,
    controller,
  }: {
    url: string;
    controller: WhiteboardController;
  }) {
    if (!url.match(URL_REGEX)) {
      return;
    }

    await this.handleClipboardContents(controller, parseUrl(controller, url));
  }

  static async handleClipboardContents(
    controller: WhiteboardController,
    clipboardContents: ParsedRecordData | undefined
  ) {
    if (!clipboardContents) {
      debug('No clipboard contents. Doing nothing.');
      return;
    }

    const position = controller.mousePosition;

    if (clipboardContents.type === 'recordReference') {
      if (!controller.props.allowUpdate) {
        // TODO maybe tell the user they weren't allowed?
        return;
      }

      controller.integrationsController.actionAddRecord(
        clipboardContents,
        position
      );
      return;
    }

    await this.pasteContents({
      contents: clipboardContents.contents,
      controller,
      position,
    });
  }

  // Moves all the objects in docData to as if they were selected and dragged to
  // the new position.
  //
  // In other workds, find the top left of the bounding box around the objects
  // in the doc data, then move each object in docData be the same distance from
  // the new position as it was from the top left of the bounding box.
  private static moveObjectsToPosition(
    docData: DocData,
    newPosition: fabric.IPoint
  ) {
    let newDocTop = Infinity;
    let newDocLeft = Infinity;

    Object.values(docData.objects).forEach(obj => {
      newDocTop = Math.min(newDocTop, obj.top ?? Infinity);
      newDocLeft = Math.min(newDocLeft, obj.left ?? Infinity);

      for (const point of obj.points ?? []) {
        newDocTop = Math.min(newDocTop, point.y ?? Infinity);
        newDocLeft = Math.min(newDocLeft, point.x ?? Infinity);
      }
    });

    const leftOffset = newPosition.x - newDocLeft;
    const topOffset = newPosition.y - newDocTop;

    Object.values(docData.objects).forEach(obj => {
      if (obj.left !== undefined && obj.top !== undefined) {
        obj.left += leftOffset;
        obj.top += topOffset;
      }
      for (const point of obj.points ?? []) {
        if (point.x !== undefined && point.y !== undefined) {
          point.x += leftOffset;
          point.y += topOffset;
        }
      }
    });
  }

  static async pasteContents({
    contents,
    controller,
    position,
  }: {
    contents: PastedWhiteboardContent;
    controller: WhiteboardController;
    position: IPoint;
  }) {
    const { canvas } = controller;

    if (contents.customShapes) {
      if (!controller.props.allowUpdate) {
        controller.actionShowMessage(
          'You do not have permission to add custom shapes to this whiteboard.'
        );
      } else if (contents.whiteboardId === controller.whiteboardId) {
        controller.actionShowMessage(
          "Can't add new custom shapes into the whiteboard they come from."
        );
      } else {
        Object.values(contents.customShapes).forEach(data => {
          const customShape = new CustomShape({
            name: data.name,
            objects: data.objects,
          });
          controller.dataStoreCustomShapes.addRecord(customShape);
        });

        controller.actionShowMessage(
          `${
            Object.keys(contents.customShapes).length
          } custom shapes created. You can find them in the shapes menu.`
        );
        // Open the shapes menu if it's not already open. If it's pinned open we don't
        // do anything. I tried to get it to unpin, close, and reopen but I could only
        // get it to unpin. Setting another stylus as active didn't close the menu even
        // after it was unpinned.
        // I don't think it's worth chasing what should be a prety rare case.
        controller.actionSetStylusMenu(controller.currentShapeStylus);
      }
    }

    // Reviewers can only paste their own comments.
    if (!controller.props.allowUpdate) {
      for (const obj of Object.values(contents.objects) as fabric.Object[]) {
        if (
          obj.type !== 'comment' ||
          obj.authorId !== controller.state.userId
        ) {
          delete contents.objects[obj.id];
        }
      }
    }

    if (!Object.keys(contents.objects).length) {
      return;
    }

    // Create a copy of the clipboard with remapped IDs.
    const newDoc = await Serialization.cloneDocumentWithAttachments(
      contents,
      controller
    );

    controller.presentationController.prepareFramesForPaste(newDoc);

    // Move all the objects to their new position by manually updating the
    // coordinates in the doc. We do this before it is ever inserted on the
    // canvas to avoid any unwanted side effects of placing the content in
    // the wrong place before moving it.
    this.moveObjectsToPosition(newDoc, position);

    // Restore the document back onto the canvas.
    Serialization.restoreDocument(controller, newDoc);

    // Select the pasted objects.
    const selectedObjects = Serialization.getUngroupedObjects(canvas, newDoc);

    const activeSelection = canvas.makeActiveSelection(selectedObjects);

    // Make sure the pasted objects are in the front.
    activeSelection.bringToFront();

    // Fire created events on the pasted objects. This gives frames a chance
    // to contain the new objects and hidden comments can be shown in the
    // side panel.
    selectedObjects.forEach(o => {
      fireCreatedDeep(o);
    });

    // Update the toolbar to appear in the right place.
    controller.actionShowObjectToolbar();
    this.toolActionComplete({ controller });
  }

  static async readImage({
    controller,
    blob,
    type,
  }: {
    controller: WhiteboardController;
    blob: Blob;
    type: string;
  }) {
    const file = new File([blob], 'Pasted image', { type });
    controller.actionUploadImage(file, controller.mousePosition);
  }

  static async action({ controller }: { controller: WhiteboardController }) {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            this.readImage({ controller, blob, type });
            break;
          }
          if (type === 'text/plain') {
            this.readText({ controller });
            break;
          }
        }
      }
      // It wasn't possible to read from the clipboard. Either the user denied
      // access or the browser doesn't allow reading from it. readText() will
      // detect this and fall back to pasting the last thing copied in this
      // whiteboard instance.
    } catch {
      this.readText({ controller });
    }

    this.toolActionComplete({ controller });
  }

  static enabled() {
    return true;
  }
}

export default ToolPaste;
